import { Injectable } from '@angular/core';

/**
 * Persists mat-paginator "entries per page" selections per user and per table.
 * Uses localStorage so preferences survive navigation, refresh, and re-login on the same browser.
 */
@Injectable({
    providedIn: 'root',
})
export class PageSizePreferenceService {
    private static readonly STORAGE_PREFIX = 'mb.pageSize';

    getPageSize(tableKey: string, pageSizeOptions: readonly number[] = [], defaultPageSize = 10): number {
        if (!tableKey) {
            return defaultPageSize;
        }

        const storedValue = this.readStoredValue(tableKey);
        const pageSize = parseInt(storedValue, 10);

        if (!Number.isFinite(pageSize) || pageSize <= 0) {
            return defaultPageSize;
        }

        if (pageSizeOptions?.length && !pageSizeOptions.includes(pageSize)) {
            return defaultPageSize;
        }

        return pageSize;
    }

    setPageSize(tableKey: string, pageSize: number): void {
        if (!tableKey || !Number.isFinite(pageSize) || pageSize <= 0) {
            return;
        }

        localStorage.setItem(this.getUserScopedKey(tableKey), pageSize.toString());

        // Keep legacy unscoped keys in sync for gradual migration of older readers.
        const legacyKey = this.getLegacyKey(tableKey);
        if (legacyKey) {
            localStorage.setItem(legacyKey, pageSize.toString());
        }
    }

    private readStoredValue(tableKey: string): string | null {
        const userScoped = localStorage.getItem(this.getUserScopedKey(tableKey));
        if (userScoped != null) {
            return userScoped;
        }

        const legacyKey = this.getLegacyKey(tableKey);
        if (legacyKey) {
            return localStorage.getItem(legacyKey);
        }

        // Allow callers to pass the legacy key itself as tableKey.
        return localStorage.getItem(tableKey);
    }

    private getUserScopedKey(tableKey: string): string {
        return `${PageSizePreferenceService.STORAGE_PREFIX}.${this.getUserId()}.${tableKey}`;
    }

    private getUserId(): string {
        try {
            const userProfile = JSON.parse(localStorage.getItem('userProfile') || 'null');
            return userProfile?.id ? String(userProfile.id) : 'anonymous';
        } catch {
            return 'anonymous';
        }
    }

    /**
     * Maps new stable table keys to pre-existing localStorage keys so users keep their preferences.
     */
    private getLegacyKey(tableKey: string): string | null {
        const legacyKeys: Record<string, string> = {
            'all-modules.executions-list': 'allExecutionsListPageSize',
            'all-modules.list': 'allModulesListPageSize',
            'all-users.active-user-list': 'allActiveUsersListPageSize',
            'all-users.community-active-user-list': 'communityActiveUsersListPageSize',
            'module-participants.list': 'moduleFormParticipantsListPageSize',
            'module-participants.module-e-learning-list': 'moduleFormParticipantsListPageSize',
            'module-participants.progress-list': 'moduleFormParticipantsProgressListPageSize',
            'course-offering-participants.list': 'courseOfferingParticipantsListPageSize',
            'module-form-gradebook-tab-grades': 'moduleFormGradebookPageSize',
            'module-form-gradebook-tab-graded-unit-page': 'moduleFormGradebookPageSize',
            'module-form-gradebook-tab-final-grade-page': 'moduleFormGradebookPageSize',
        };

        return legacyKeys[tableKey] || null;
    }
}
